import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Cancel } from "@material-ui/icons";

import { FEATURES, useAccess } from "../../hooks/useAccess";
import {
  clearIpAccountLookup,
  createBlockedIp,
  deleteBlockedIp,
  deleteIpOverride,
  getAccountsForIp,
  getIpManagement,
  saveIpOverride,
  updateIpDefaultLimit,
  updateRegistrationEmailProviders,
} from "../../store/ipManagement/action";
import { Toast } from "../../util/Toast";
import { warningv2 } from "../../util/Alert";

const emptyOverride = { ip: "", maxAccounts: "", reason: "" };
const emptyBlock = { ip: "", reason: "" };
const fallbackEmailProviders = ["gmail.com", "yahoo.com", "icloud.com"];
const emailDomainPattern =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const isValidIp = (value) => {
  const ip = value.trim();
  const ipv4Parts = ip.split(".");
  if (
    ipv4Parts.length === 4 &&
    ipv4Parts.every(
      (part) =>
        /^\d{1,3}$/.test(part) && Number(part) >= 0 && Number(part) <= 255,
    )
  ) {
    return true;
  }

  if (!ip.includes(":") || !/^[0-9a-fA-F:.]+$/.test(ip)) return false;
  try {
    return new URL(`http://[${ip}]/`).hostname.length > 2;
  } catch (_error) {
    return false;
  }
};

const formatAdmin = (admin) => admin?.name || admin?.email || "System";
const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "Not set";

const PolicyDialog = ({
  open,
  title,
  description,
  onClose,
  children,
  actions,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="ip-policy-dialog-title"
    fullWidth
    maxWidth="md"
  >
    <DialogTitle id="ip-policy-dialog-title">
      <span className="text-danger font-weight-bold h4">{title}</span>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        style={{ position: "absolute", right: 8, top: 8 }}
      >
        <Tooltip title="Close">
          <Cancel className="text-danger" />
        </Tooltip>
      </IconButton>
    </DialogTitle>
    <DialogContent>
      {description && <p className="text-muted mb-4">{description}</p>}
      {children}
    </DialogContent>
    <DialogActions className="p-3">{actions}</DialogActions>
  </Dialog>
);

const FieldError = ({ children }) =>
  children ? <small className="text-danger">{children}</small> : null;

const IPManagement = () => {
  const dispatch = useDispatch();
  const { canEdit, isSuperAdmin } = useAccess();
  const canManage = isSuperAdmin || canEdit(FEATURES.BACKEND_IP_MANAGEMENT);
  const {
    data,
    loading,
    loadError,
    saving,
    accountLookupLoading,
    accountLookupResult,
    accountLookupError: accountLookupRequestError,
  } = useSelector((state) => state.ipManagement);
  const [defaultLimit, setDefaultLimit] = useState("5");
  const [overrideForm, setOverrideForm] = useState(emptyOverride);
  const [editingOverrideId, setEditingOverrideId] = useState(null);
  const [blockForm, setBlockForm] = useState(emptyBlock);
  const [errors, setErrors] = useState({});
  const [activeDialog, setActiveDialog] = useState(null);
  const [accountLookupIp, setAccountLookupIp] = useState("");
  const [accountLookupValidationError, setAccountLookupValidationError] =
    useState("");
  const [emailProvidersText, setEmailProvidersText] = useState("");
  const accountLookupError =
    accountLookupValidationError || accountLookupRequestError;

  useEffect(() => {
    dispatch(getIpManagement());
  }, [dispatch]);

  const totalCoveredAccounts = useMemo(
    () =>
      data.overrides.reduce(
        (total, item) => total + item.currentAccountCount,
        0,
      ),
    [data.overrides],
  );

  const validateLimit = (value) =>
    Number.isInteger(Number(value)) && Number(value) >= 1;

  const saveDefaultLimit = async () => {
    if (!validateLimit(defaultLimit)) {
      setErrors({ defaultLimit: "Enter an integer of at least 1." });
      return;
    }
    setErrors({});
    const succeeded = await dispatch(
      updateIpDefaultLimit(Number(defaultLimit)),
    );
    if (succeeded) setActiveDialog(null);
  };

  const validateOverride = () => {
    const nextErrors = {};
    if (!isValidIp(overrideForm.ip))
      nextErrors.overrideIp = "Enter a valid exact IP address.";
    if (!validateLimit(overrideForm.maxAccounts))
      nextErrors.overrideLimit = "Enter an integer of at least 1.";
    if (!overrideForm.reason.trim())
      nextErrors.overrideReason = "Reason is required.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveOverride = async () => {
    if (!validateOverride()) return;
    const payload = {
      ip: overrideForm.ip.trim(),
      maxAccounts: Number(overrideForm.maxAccounts),
      reason: overrideForm.reason.trim(),
    };
    const succeeded = await dispatch(
      saveIpOverride(editingOverrideId, payload),
    );
    if (succeeded) {
      setOverrideForm(emptyOverride);
      setEditingOverrideId(null);
      setErrors({});
      setActiveDialog(null);
    }
  };

  const validateBlock = () => {
    const nextErrors = {};
    if (!isValidIp(blockForm.ip))
      nextErrors.blockIp = "Enter a valid exact IP address.";
    if (!blockForm.reason.trim())
      nextErrors.blockReason = "Reason is required.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveBlockedIp = async () => {
    if (!validateBlock()) return;
    const succeeded = await dispatch(
      createBlockedIp({
        ip: blockForm.ip.trim(),
        reason: blockForm.reason.trim(),
      }),
    );
    if (succeeded) {
      setBlockForm(emptyBlock);
      setErrors({});
      setActiveDialog(null);
    }
  };

  const saveEmailProviders = async () => {
    const allowedDomains = [
      ...new Set(
        emailProvidersText
          .split(/[\n,]+/)
          .map((domain) => domain.trim().toLowerCase().replace(/^@/, ""))
          .filter(Boolean),
      ),
    ];
    if (!allowedDomains.length) {
      setErrors({
        emailProviders: "Enter at least one email provider domain.",
      });
      return;
    }
    if (allowedDomains.some((domain) => !emailDomainPattern.test(domain))) {
      setErrors({ emailProviders: "Every entry must be a valid domain name." });
      return;
    }

    setErrors({});
    const succeeded = await dispatch(
      updateRegistrationEmailProviders(allowedDomains),
    );
    if (succeeded) setActiveDialog(null);
  };

  const closeDialog = () => {
    if (saving) return;
    setActiveDialog(null);
    setEditingOverrideId(null);
    setOverrideForm(emptyOverride);
    setBlockForm(emptyBlock);
    setAccountLookupIp("");
    setAccountLookupValidationError("");
    dispatch(clearIpAccountLookup());
    setEmailProvidersText("");
    setErrors({});
  };

  const openDefaultDialog = () => {
    setDefaultLimit(String(data.policy.defaultMaxAccounts));
    setErrors({});
    setActiveDialog("default");
  };

  const openOverrideDialog = () => {
    setEditingOverrideId(null);
    setOverrideForm(emptyOverride);
    setErrors({});
    setActiveDialog("override");
  };

  const beginEditOverride = (override) => {
    setEditingOverrideId(override._id);
    setOverrideForm({
      ip: override.ip,
      maxAccounts: String(override.maxAccounts),
      reason: override.reason,
    });
    setErrors({});
    setActiveDialog("override");
  };

  const openBlockDialog = () => {
    setBlockForm(emptyBlock);
    setErrors({});
    setActiveDialog("block");
  };

  const openAccountLookupDialog = () => {
    setAccountLookupIp("");
    setAccountLookupValidationError("");
    dispatch(clearIpAccountLookup());
    setActiveDialog("accounts");
  };

  const openEmailProvidersDialog = () => {
    const allowedDomains =
      data.emailProviderPolicy?.allowedDomains || fallbackEmailProviders;
    setEmailProvidersText(allowedDomains.join("\n"));
    setErrors({});
    setActiveDialog("email-providers");
  };

  const lookupAccounts = async () => {
    if (!isValidIp(accountLookupIp)) {
      setAccountLookupValidationError(
        "Enter a valid exact IPv4 or IPv6 address.",
      );
      dispatch(clearIpAccountLookup());
      return;
    }

    setAccountLookupValidationError("");
    await dispatch(getAccountsForIp(accountLookupIp.trim()));
  };

  const confirmDelete = async ({ title, text, action }) => {
    if (!canManage)
      return Toast("error", "You have read-only access to IP Management.");
    const confirmed = await warningv2(title, text);
    if (confirmed) await dispatch(action);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-danger" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (loadError || !data.policy) {
    return (
      <div className="card">
        <div className="card-body text-center py-5" role="alert">
          <p>{loadError || "Unable to load IP policies."}</p>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => dispatch(getIpManagement())}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-3 text-white">IP Management</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-start float-lg-end"
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  IP Management
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="card stats-card">
            <div className="card-body">
              <div className="stats-info">
                <h5 className="card-title">{data.policy.defaultMaxAccounts}</h5>
                <p className="stats-text">Global Default Max Accounts per IP</p>
              </div>
              <div className="stats-icon">
                <i className="material-icons">public</i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="card stats-card">
            <div className="card-body">
              <div className="stats-info">
                <h5 className="card-title">{data.overrides.length}</h5>
                <p className="stats-text">IP Overrides</p>
              </div>
              <div className="stats-icon">
                <i className="material-icons">tune</i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="card stats-card">
            <div className="card-body">
              <div className="stats-info">
                <h5 className="card-title">{data.blockedIps.length}</h5>
                <p className="stats-text">Blocked IPs</p>
              </div>
              <div className="stats-icon">
                <i className="material-icons">block</i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-header pb-0">
              <div className="row my-3 align-items-center">
                <div className="col-md-8">
                  <h4 className="card-title mb-0">Registration Overrides</h4>
                </div>
                <div className="col-md-4 text-md-end mt-3 mt-md-0">
                  <button
                    type="button"
                    className="btn waves-effect waves-light btn-danger btn-sm"
                    disabled={!canManage}
                    onClick={openOverrideDialog}
                  >
                    <i className="fa fa-plus" />
                    <span className="icon_margin">Add IP Override</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body card-overflow">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="text-center">
                    <tr>
                      <th>IP Address</th>
                      <th>Accounts</th>
                      <th>Effective Cap</th>
                      <th>Reason</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {data.overrides.length ? (
                      data.overrides.map((override) => (
                        <tr key={override._id}>
                          <td>
                            <code>{override.ip}</code>
                          </td>
                          <td>{override.currentAccountCount}</td>
                          <td>{override.effectiveMaxAccounts}</td>
                          <td>{override.reason}</td>
                          <td>
                            {formatDate(override.updatedAt)}
                            <div className="text-muted small">
                              {formatAdmin(override.updatedBy)}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-info btn-sm me-2"
                              disabled={!canManage}
                              onClick={() => beginEditOverride(override)}
                            >
                              <i className="fas fa-edit" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={
                                !canManage ||
                                saving === `delete-override-${override._id}`
                              }
                              onClick={() =>
                                confirmDelete({
                                  title: "Remove this IP override?",
                                  text: "The global default will apply immediately.",
                                  action: deleteIpOverride(override._id),
                                })
                              }
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6">Nothing to show!!</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <div className="card">
            <div className="card-header pb-0">
              <div className="row my-3 align-items-center">
                <div className="col-md-8">
                  <h4 className="card-title mb-0">Blocked IPs</h4>
                </div>
                <div className="col-md-4 text-md-end mt-3 mt-md-0">
                  <button
                    type="button"
                    className="btn waves-effect waves-light btn-danger btn-sm"
                    disabled={!canManage}
                    onClick={openBlockDialog}
                  >
                    <i className="fa fa-plus" />
                    <span className="icon_margin">Block an IP</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body card-overflow">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="text-center">
                    <tr>
                      <th>IP Address</th>
                      <th>Existing Accounts</th>
                      <th>Reason</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {data.blockedIps.length ? (
                      data.blockedIps.map((blockedIp) => (
                        <tr key={blockedIp._id}>
                          <td>
                            <code>{blockedIp.ip}</code>
                          </td>
                          <td>{blockedIp.currentAccountCount}</td>
                          <td>{blockedIp.reason}</td>
                          <td>
                            {formatDate(blockedIp.createdAt)}
                            <div className="text-muted small">
                              {formatAdmin(blockedIp.createdBy)}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={
                                !canManage ||
                                saving === `unblock-${blockedIp._id}`
                              }
                              onClick={() =>
                                confirmDelete({
                                  title: "Unblock this IP?",
                                  text: "Login and signup will be allowed again immediately.",
                                  action: deleteBlockedIp(blockedIp._id),
                                })
                              }
                            >
                              Unblock
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5">Nothing to show!!</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <div className="card">
            <div className="card-header pb-0">
              <h4 className="card-title">Other Actions</h4>
            </div>
            <div className="card-body">
              <p className="text-muted">
                Update the global registration policy or inspect the accounts
                registered to an exact IP address.
              </p>
              <button
                type="button"
                className="btn btn-danger me-2 mb-2"
                disabled={!canManage}
                onClick={openDefaultDialog}
              >
                Edit Max Accounts per IP
              </button>
              <button
                type="button"
                className="btn btn-info me-2 mb-2"
                onClick={openAccountLookupDialog}
              >
                View Accounts by IP
              </button>
              <button
                type="button"
                className="btn btn-success mb-2"
                disabled={!canManage}
                onClick={openEmailProvidersDialog}
              >
                Manage Email Providers
              </button>
            </div>
          </div>
        </div>
      </div>

      <PolicyDialog
        open={activeDialog === "default"}
        title="Edit Global Default"
        description="This limit applies immediately to every IP without a custom override."
        onClose={closeDialog}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={saving === "default"}
              onClick={saveDefaultLimit}
            >
              {saving === "default" ? "Saving…" : "Save Default"}
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label
            htmlFor="default-account-limit"
            className="form-label text-white"
          >
            Maximum Accounts per IP
          </label>
          <input
            id="default-account-limit"
            type="number"
            min="1"
            step="1"
            autoFocus
            className="form-control"
            value={defaultLimit}
            onChange={(event) => setDefaultLimit(event.target.value)}
          />
          <FieldError>{errors.defaultLimit}</FieldError>
        </div>
        <small className="text-muted">
          Last updated {formatDate(data.policy.updatedAt)} by{" "}
          {formatAdmin(data.policy.updatedBy)}
        </small>
      </PolicyDialog>

      <PolicyDialog
        open={activeDialog === "override"}
        title={editingOverrideId ? "Edit IP Override" : "Add IP Override"}
        description="Set a permanent capacity for one exact IPv4 or IPv6 address."
        onClose={closeDialog}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={saving === "override"}
              onClick={saveOverride}
            >
              {saving === "override"
                ? "Saving…"
                : editingOverrideId
                  ? "Save Changes"
                  : "Add Override"}
            </button>
          </>
        }
      >
        <div className="row text-white">
          <div className="col-md-7 mb-3">
            <label className="form-label">IP Address</label>
            <input
              autoFocus
              className="form-control"
              value={overrideForm.ip}
              placeholder="203.0.113.25"
              onChange={(event) =>
                setOverrideForm({ ...overrideForm, ip: event.target.value })
              }
            />
            <FieldError>{errors.overrideIp}</FieldError>
          </div>
          <div className="col-md-5 mb-3">
            <label className="form-label">Maximum Accounts</label>
            <input
              type="number"
              min="1"
              step="1"
              className="form-control"
              value={overrideForm.maxAccounts}
              placeholder="12"
              onChange={(event) =>
                setOverrideForm({
                  ...overrideForm,
                  maxAccounts: event.target.value,
                })
              }
            />
            <FieldError>{errors.overrideLimit}</FieldError>
          </div>
          <div className="col-12 mb-3">
            <label className="form-label">Reason</label>
            <textarea
              rows="3"
              maxLength="500"
              className="form-control"
              value={overrideForm.reason}
              placeholder=""
              onChange={(event) =>
                setOverrideForm({ ...overrideForm, reason: event.target.value })
              }
            />
            <FieldError>{errors.overrideReason}</FieldError>
          </div>
        </div>
      </PolicyDialog>

      <PolicyDialog
        open={activeDialog === "block"}
        title="Block an IP Address"
        description="Users on this exact IP will be denied at login and signup."
        onClose={closeDialog}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={saving === "block"}
              onClick={saveBlockedIp}
            >
              {saving === "block" ? "Blocking…" : "Block IP"}
            </button>
          </>
        }
      >
        <div className="mb-3 text-white">
          <label className="form-label">IP Address</label>
          <input
            autoFocus
            className="form-control"
            value={blockForm.ip}
            placeholder="198.51.100.14"
            onChange={(event) =>
              setBlockForm({ ...blockForm, ip: event.target.value })
            }
          />
          <FieldError>{errors.blockIp}</FieldError>
        </div>
        <div className="mb-3 text-white">
          <label className="form-label">Reason</label>
          <textarea
            rows="3"
            maxLength="500"
            className="form-control"
            value={blockForm.reason}
            placeholder=""
            onChange={(event) =>
              setBlockForm({ ...blockForm, reason: event.target.value })
            }
          />
          <FieldError>{errors.blockReason}</FieldError>
        </div>
      </PolicyDialog>

      <PolicyDialog
        open={activeDialog === "accounts"}
        title="View Accounts by IP"
        description="Enter one exact IP address to view the accounts registered to it."
        onClose={closeDialog}
        actions={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeDialog}
          >
            Close
          </button>
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            lookupAccounts();
          }}
        >
          <label htmlFor="account-lookup-ip" className="form-label text-white">
            IP Address
          </label>
          <div className="input-group mb-2">
            <input
              id="account-lookup-ip"
              autoFocus
              className="form-control"
              value={accountLookupIp}
              placeholder="203.0.113.25"
              onChange={(event) => setAccountLookupIp(event.target.value)}
            />
            <button
              type="submit"
              className="btn btn-danger"
              disabled={accountLookupLoading}
            >
              {accountLookupLoading ? "Searching…" : "Search"}
            </button>
          </div>
          <FieldError>{accountLookupError}</FieldError>
        </form>

        {accountLookupResult && (
          <div className="mt-4 text-white">
            <div className="d-flex flex-wrap justify-content-between mb-3">
              <strong>{accountLookupResult.ip}</strong>
              <span className="text-muted">
                {accountLookupResult.registrationAccountCount} registered ·{" "}
                {accountLookupResult.lastLoginAccountCount} last logged in · Cap{" "}
                {accountLookupResult.effectiveMaxAccounts}
                {accountLookupResult.isBlocked ? " · Blocked" : ""}
              </span>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="text-center">
                  <tr>
                    <th>Username</th>
                    <th>Unique ID</th>
                    <th>Email</th>
                    <th>Match</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {accountLookupResult.accounts.length ? (
                    accountLookupResult.accounts.map((account) => (
                      <tr key={account._id}>
                        <td>{account.username || "-"}</td>
                        <td>{account.uniqueId || "-"}</td>
                        <td>{account.email || "-"}</td>
                        <td>
                          {account.matchesRegistrationIp &&
                          account.matchesLastLoginIp
                            ? "Registration + last login"
                            : account.matchesRegistrationIp
                              ? "Registration"
                              : "Last login"}
                        </td>
                        <td>{formatDate(account.createdAt)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No accounts found for this IP.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </PolicyDialog>

      <PolicyDialog
        open={activeDialog === "email-providers"}
        title="Allowed Registration Email Providers"
        description="Only new email registrations must match one of these exact domains."
        onClose={closeDialog}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeDialog}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={saving === "email-providers"}
              onClick={saveEmailProviders}
            >
              {saving === "email-providers" ? "Saving…" : "Save Providers"}
            </button>
          </>
        }
      >
        <div className="mb-3 text-white">
          <label htmlFor="allowed-email-providers" className="form-label">
            Allowed Domains
          </label>
          <textarea
            id="allowed-email-providers"
            autoFocus
            rows="6"
            className="form-control"
            value={emailProvidersText}
            placeholder={fallbackEmailProviders.join("\n")}
            onChange={(event) => setEmailProvidersText(event.target.value)}
          />
          <FieldError>{errors.emailProviders}</FieldError>
          <small className="text-muted d-block mt-2">
            Enter one domain per line or separate domains with commas.
          </small>
        </div>
      </PolicyDialog>
    </>
  );
};

export default IPManagement;
